import { faBarcodeRead } from '@fortawesome/pro-solid-svg-icons';
import { faCircleInfo } from '@fortawesome/pro-solid-svg-icons';
import { faCrateApple } from '@fortawesome/pro-solid-svg-icons';
import { faDiagramSankey } from '@fortawesome/pro-solid-svg-icons';
import { faStore } from '@fortawesome/pro-solid-svg-icons';
import { faTag } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useState } from 'react';
import { Button, ListGroup } from 'react-bootstrap';
import { Input } from 'reactstrap';
import { tss } from 'tss-react';

import { RecipeErpViewComponentProps } from 'components/desktop/ShoppingCart/Detail/Index';
import NutrientsData from 'components/desktop/ShoppingCart/Detail/misc/NutrientsData';
import SourcesDiagram from 'components/desktop/ShoppingCart/Detail/misc/SourcesDiagram';
import Modal from 'components/desktop/_general/Modal/Modal';
import useCurrency from 'hooks/redux/useCurrency';
import useTranslation from 'hooks/useTranslation';
import { Product, renderWeight } from 'types/ShoppingCart/Product';

interface IProps extends RecipeErpViewComponentProps {
	product: Product.Type;
}

const ProductContent = ({ product, cart }: IProps) => {
	const { tl, t } = useTranslation();
	const currency = useCurrency();
	const { classes } = useStyles();
	const [sourcesModalisOpen, setSourcesModalIsOpen] = useState<boolean>(false);
	const [nutrientsModalIsOpen, setNutrientsModalIsOpen] = useState<boolean>(false);

	const contentMetadata = (): JSX.Element => (
		<div className="col-md-4 d-flex flex-column">
			<ListGroup>
				<ListGroup.Item style={{ backgroundColor: '#fafafa' }}>
					<span>
						<FontAwesomeIcon
							icon={faBarcodeRead}
							style={{ marginRight: '1rem' }}
						></FontAwesomeIcon>
						<span>{product.store === 'Default' ? '***' : product.gtin}</span>
					</span>
				</ListGroup.Item>
				<ListGroup.Item style={{ backgroundColor: '#fafafa' }}>
					<span>
						<FontAwesomeIcon
							icon={faStore}
							style={{ marginRight: '1rem' }}
						></FontAwesomeIcon>
						{product.store}
					</span>
				</ListGroup.Item>
				<ListGroup.Item style={{ backgroundColor: '#fafafa' }}>
					<span>
						<FontAwesomeIcon
							icon={faCrateApple}
							style={{ marginRight: '1rem' }}
						></FontAwesomeIcon>
						<span>{tl(product.ingredient.name)}</span>
					</span>
				</ListGroup.Item>
				<ListGroup.Item style={{ backgroundColor: '#fafafa' }}>
					<span>
						<FontAwesomeIcon
							icon={faTag}
							style={{ marginRight: '1rem' }}
						></FontAwesomeIcon>
						<span>
							{product.price.toFixed(2)} {currency}
						</span>{' '}
						{t('measurments:PER_PIECE')}
					</span>
				</ListGroup.Item>
			</ListGroup>
		</div>
	);

	const contentActions = (): JSX.Element => (
		<div className={`col-md-4 ${classes.contentActions}`}>
			<Modal
				isOpen={sourcesModalisOpen}
				close={() => setSourcesModalIsOpen(false)}
				headerIcon={<FontAwesomeIcon icon={faDiagramSankey}></FontAwesomeIcon>}
				bodyContent={
					<SourcesDiagram
						cart={cart}
						isOpen={sourcesModalisOpen}
						onHide={() => setSourcesModalIsOpen(false)}
						product={product}
					></SourcesDiagram>
				}
				headerText={`${tl(product.ingredient.name)} ${t('shoppingCart:SOURCES')}`}
				buttonFull={{
					label: t('_general:BTN_LEAVE'),
					onClick: () => setSourcesModalIsOpen(false),
				}}
				wide={true}
			/>
			<Modal
				isOpen={nutrientsModalIsOpen}
				close={() => setNutrientsModalIsOpen(false)}
				headerIcon={<FontAwesomeIcon icon={faCircleInfo}></FontAwesomeIcon>}
				bodyContent={<NutrientsData nutrients={product.nutrients}></NutrientsData>}
				headerText={`${tl(product.ingredient.name)} ${t('_general:NUTRIENTS')}`}
				buttonFull={{
					label: t('_general:BTN_LEAVE'),
					onClick: () => setNutrientsModalIsOpen(false),
				}}
				wide={true}
			/>
			<Button
				className="mt-3 mb-3"
				variant="outline-secondary"
				onClick={() => setNutrientsModalIsOpen(true)}
			>
				<FontAwesomeIcon
					icon={faCircleInfo}
					style={{
						marginRight: '6px',
					}}
				></FontAwesomeIcon>
				{t('_general:NUTRIENTS')}
			</Button>
			<Button
				className="mb-3"
				variant="outline-secondary"
				onClick={() => setSourcesModalIsOpen(true)}
			>
				<FontAwesomeIcon
					icon={faDiagramSankey}
					style={{
						marginRight: '6px',
					}}
				></FontAwesomeIcon>
				{t('shoppingCart:SOURCES')}
			</Button>
		</div>
	);

	const contentCalculations = (): JSX.Element => (
		<div className={`col-md-4 d-flex flex-column ${classes.contentCalculations}`}>
			<div className="input-group input-group-sm mb-4">
				<span
					className="input-group-text"
					id="inputGroup-sizing-sm"
					style={{ width: '5rem' }}
				>
					{t('shoppingCart:QUANTITY')}
				</span>
				<input
					type="number"
					className="form-control"
					value={Product.getQuantity(product) || 0}
					disabled
				/>
				<span
					className="input-group-text"
					id="inputGroup-sizing-sm"
					style={{ width: '5rem' }}
				>
					{t('shoppingCart:PIECES')}
				</span>
			</div>
			<div className="input-group input-group-sm mb-4">
				<span
					className="input-group-text"
					id="inputGroup-sizing-sm"
					style={{ width: '5rem' }}
				>
					{t('shoppingCart:SIZE')}
				</span>
				<Input
					type="text"
					value={renderWeight(product.size, product, false)}
					disabled
				></Input>
				<span className="input-group-text" id="inputGroup-sizing-sm">
					{Product.getUnit(product)}
				</span>
			</div>
			<div className="input-group input-group-sm">
				<span
					className="input-group-text"
					id="inputGroup-sizing-sm"
					style={{ width: '5rem' }}
				>
					{t('shoppingCart:WEIGHT')}
				</span>
				<Input
					type="text"
					value={renderWeight(product.weight, product, false)}
					disabled
				></Input>
				<span className="input-group-text" id="inputGroup-sizing-sm">
					{Product.getUnit(product)}
				</span>
			</div>
		</div>
	);

	return (
		<div>
			<div className="row d-flex align-items-center">
				{contentMetadata()}
				{contentActions()}
				{contentCalculations()}
			</div>
		</div>
	);
};

export default ProductContent;

export const useStyles = tss.create({
	contentActions: {
		borderLeft: '2px solid #ccc',
		borderRight: '2px solid #ccc',
		display: 'flex',
		flexDirection: 'column',
		justifyContent: 'space-between',
		paddingLeft: '2rem',
		paddingRight: '2rem',
	},
	contentCalculations: {
		display: 'flex',
		flexDirection: 'column',
		justifyContent: 'space-between',
	},
});
